import { exceptionLogger, log, logException } from '@paradigm/logging';
import { AsyncResp } from '@paradigm/api/src/fetch-api';
import { setCounterpartyFavoritism } from '@paradigm/api/src/contacts';
import {
  createReadonlyObservable,
  mapObservable,
  ReadonlyObservable,
} from '@paradigm/utils/src/createObservable';

import {
  Counterparty,
  DrfqVenueCode,
} from '#/unified-rfqs/entities/drfq/domain';
import { ListCounterpartiesReq } from '#/unified-rfqs/repositories/drfq-types';
import { getCounterparties } from '#/unified-rfqs/repositories/drfq-api';
import EntityStore, { PublicEntityStore } from '#/unified-rfqs/store/entity';

import createCpsSearch, { CpsSearch } from './counterparties/cps-search';

export type State =
  | { kind: 'initial' }
  | { kind: 'loading'; venue: DrfqVenueCode }
  | { kind: 'loaded'; venue: DrfqVenueCode }
  | { kind: 'error'; message: string };

export interface StoredCp {
  /** Desk name is used as the unique identifier */
  readonly id: string;
  /**
   * Identifier from our old API that we're using only for favorites
   * persistence.
   *
   * Do NOT rely on this for anything else other than favorites feature.
   */
  readonly legacyId: number;
  readonly data: Counterparty;
  /** Timestamp of selection time, `0` for not selected */
  readonly selectedAt: number;
  readonly isFavorite: boolean;
}

export function createCounterpartiesService() {
  return new CounterpartiesService();
}

export type PublicCounterpartiesService = Readonly<
  Omit<CounterpartiesService, 'teardown'>
>;

type PublicCpsSearch = Pick<CpsSearch, 'results' | 'setDebounced'>;

export class CounterpartiesService {
  readonly state: ReadonlyObservable<State>;
  private readonly setState: (value: State) => void;

  private readonly _store = new EntityStore({ sortComparer: compareStoredCp });
  readonly store: PublicEntityStore<StoredCp> = this._store;

  private readonly _search = createCpsSearch({
    orderedDesks: this._store.listIds(),
  });
  readonly search: PublicCpsSearch = this._search;

  private readonly clearSelectedSubscription: () => void;
  readonly selected: ReadonlyObservable<readonly Counterparty[]>;

  private controller = new AbortController();

  constructor() {
    const [state, setState] = createReadonlyObservable<State>(
      'counterparties-state',
      { kind: 'initial' },
    );
    this.state = state;
    this.setState = setState;

    const [clearSelectedSubscription, selected] = mapObservable(
      this._store.list(),
      takeSelected,
    );
    this.clearSelectedSubscription = clearSelectedSubscription;
    this.selected = selected;
  }

  teardown() {
    this.controller.abort();
    this._search.teardown();
    this.clearSelectedSubscription();
  }

  /**
   * Fetches all cps for the given venue. This will clear previously loaded
   * cps and abort any previous ongoing request.
   */
  async fetchCps(venue: DrfqVenueCode) {
    this.controller.abort();
    this.controller = new AbortController();
    const { signal } = this.controller;

    this._store.clear();
    this.setState({ kind: 'loading', venue });

    let resp;
    try {
      resp = await fetchAllCps({ venues: [venue], signal });
      if (signal.aborted) return;
    } catch (error: unknown) {
      if (signal.aborted) return;
      this.setState({ kind: 'error', message: (error as Error).message });
      logException(
        {
          message: 'Unexpected failure getting all counterparties',
          data: { venue },
        },
        error,
      );
      return;
    }

    if (!resp.ok) {
      this.setState({ kind: 'error', message: resp.message });
      log('Unexpected error response from counterparties endpoint', {
        level: 'error',
        data: { venue, resp },
      });
      return;
    }

    const storedCps = resp.data.map(makeStoredCp);
    this._store.addMany(storedCps);
    this._search.configure(resp.data);

    this.setState({ kind: 'loaded', venue });
  }

  /** Set/unset counterparty as selected. */
  setSelected(id: string, value: boolean, now?: number) {
    this._store.updateOne(id, {
      selectedAt: value ? now ?? Date.now() : 0,
    });
  }

  findByDeskName(deskName: string) {
    return this._store
      .list()
      .get()
      .find((cp) => cp.data.desk_name === deskName);
  }

  /** Set/unset counterparty as favorite. */
  setFavorite(id: string, isFavorite: boolean) {
    const cp = this._store.selectById(id);
    if (cp == null) return;

    this._store.updateOne(id, { isFavorite });

    setCounterpartyFavoritism({ id: cp.legacyId, isFavorite }).catch(
      exceptionLogger({
        message: 'Failed persisting cp favorite state on servers',
        data: { id, isFavorite, cp },
      }),
    );
  }
}

/** More recently selected comes first, then favorites. */
function compareStoredCp(cpA: StoredCp, cpB: StoredCp) {
  const selectedDiff = cpB.selectedAt - cpA.selectedAt;
  if (selectedDiff !== 0) {
    return selectedDiff;
  }

  if (cpA.isFavorite !== cpB.isFavorite) {
    return cpA.isFavorite ? -1 : 1;
  }

  return 0;
}

function makeStoredCp(cp: Counterparty): StoredCp {
  return {
    id: cp.desk_name,
    legacyId: cp.id,
    data: cp,
    selectedAt: 0,
    isFavorite: cp.groups?.includes('FAVORITE') ?? false,
  };
}

const PAGE_SIZE = 1000;

type ListAllCpsReq = Omit<ListCounterpartiesReq, 'cursor' | 'pageSize'>;

/** Fetch all cps by following next cursors until exhausted. */
async function fetchAllCps(
  req: ListAllCpsReq,
): AsyncResp<readonly Counterparty[]> {
  let cps: Counterparty[] = [];
  let resp: Awaited<ReturnType<typeof getCounterparties>>;
  let cursor: string | null = null;

  do {
    resp = await getCounterparties({ ...req, pageSize: PAGE_SIZE, cursor });

    if (!resp.ok) return resp;

    cps = [...cps, ...resp.data.results];
    cursor = resp.data.next;
  } while (cursor != null);

  return { ok: true, error: '', message: '', status: 200, data: cps };
}

/**
 * Take CPs that are selected.
 *
 * `storedCps` **MUST** be ordered by "selected first".
 */
function takeSelected(storedCps: readonly StoredCp[]): Counterparty[] {
  const selectedCps: Counterparty[] = [];
  for (const cp of storedCps) {
    if (cp.selectedAt === 0) break;
    selectedCps.push(cp.data);
  }
  return selectedCps;
}
